import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { config } from 'config';
import { useLocalStorage, useStore, useFeatureFlag } from 'providers';
import { ROUTES } from 'constants/routes';
import { OfferNotificationInfo, promotions } from 'constants/promotions';
import {
  installWithPromptNotification,
  winASliceNotification,
  jet2holidaysNotification,
  saleNotification,
  amountOffFoodPromotionNotification,
  offerInWalletNotification,
} from 'constants/availableNotifications';
import { Offer, SurprisePerkType, Voucher, VoucherList } from 'models';
import { retryAsyncCall } from 'helpers/api/retryCall';
import { pushAnalyticsEvent } from 'helpers/analytics';
import { getVouchersData } from 'services/loyaltyGateway';
import { isWithinDateRange } from 'utils';
import { useFetchFromService, useSessionState, useNotifications, useAddToHomeScreen } from 'hooks';
import Layout from 'layouts';
import Loader from 'components/Loader';
import UserVisits from 'components/UserVisits';
import TertiaryButton from 'components/TertiaryButton';
import OffersCarousel from 'components/OffersCarousel';
import NotificationCentre from 'components/NotificationCentre';

import {
  SurpriseAlertModal,
  PurchaseFlowModal,
  ModalState,
  PerkzWalletModal,
  ReferAndEarnModal,
  ScheduledPerkModal,
  BirthdayModal,
} from 'views/modals';

import { StyledContainer, StyledPaper, StyledProfileCard } from './Welcome.styles';

const { retries } = config;
const {
  welcomeOfferIds,
  birthdayOfferIds,
  isSaleOfferPeriod,
  isSpinTheWheelPeriod,
  percentOffFoodOfferIds,
  amountOffFoodUKOfferIds,
  amountOffFoodROIOfferIds,
  offerIdsInWallet,
} = promotions;

interface WelcomeProps {
  isNewSignUp: boolean;
}

const handleVouchersRetries = async (): Promise<VoucherList> => {
  const vouchersData = await retryAsyncCall(
    getVouchersData,
    (vouchersDataReturned) => vouchersDataReturned.active.length > 0,
    (vouchersDataReturned, numberOfRetries) => ({
      event: 'Welcome Voucher Retry Fix',
      numberOfRetryAttempts: numberOfRetries,
      isWelcomeVoucherReturned: vouchersDataReturned.active.length > 0,
    }),
    retries
  );

  return vouchersData;
};

function Welcome({ isNewSignUp }: WelcomeProps) {
  /**
   * HOOKS
   */
  const { store } = useStore();
  const { getFromStorage, setToStorage, removeFromStorage } = useLocalStorage();

  const { userData, vouchersData, offersCarouselData } = store;
  const {
    isEligibleForSurpriseVoucher,
    referralPointsEarned,
    numberOfReferrals,
    showWelcomeBonusZsNotification,
  } = userData?.loyaltyDetails || {};

  const allowSaleOffers = useFeatureFlag('allowSaleOffers') && isSaleOfferPeriod;
  const allowWinASlice = useFeatureFlag('allowWinASlice');
  const allowSpinToWin = useFeatureFlag('allowSpinToWin');
  const allowJet2holidays = useFeatureFlag('allowJet2holidays');
  const allowWelcomeBonus = useFeatureFlag('welcomeBonusNotification');
  const allowPercentOffFoodVoucherNotification = useFeatureFlag(
    'percentOffFoodVoucherNotification'
  );
  const allowAmountOffFoodVoucherNotification = useFeatureFlag('amountOffFoodVoucherNotification');
  const allowOfferInWalletNotification = useFeatureFlag('allowOfferInWalletNotification');

  const storedWelcomeNotification = !!sessionStorage.getItem('showWelcomeNotification');
  const welcomeVoucher = vouchersData?.active.find(({ offerId }: Voucher) =>
    welcomeOfferIds.includes(offerId)
  );
  const birthdayVoucher = vouchersData?.active.find(({ offerId }: Voucher) =>
    birthdayOfferIds.includes(offerId)
  );

  const percentOffFoodVoucher = vouchersData?.active.find(({ offerId }: Voucher) =>
    percentOffFoodOfferIds.includes(offerId)
  );

  const amountOffFoodVoucher = vouchersData?.active.find(({ offerId }: Voucher) =>
    [...amountOffFoodUKOfferIds, ...amountOffFoodROIOfferIds].includes(offerId)
  );

  const hasROIVouchers = vouchersData?.active.find(({ offerId }: Voucher) =>
    amountOffFoodROIOfferIds.includes(offerId)
  );

  const { title: defaultTitle, description: defaultDescription } =
    amountOffFoodPromotionNotification;
  const amountOffFoodNotificationTitle = hasROIVouchers
    ? defaultTitle.replaceAll('£', '€')
    : defaultTitle;
  const amountOffFoodNotificationDescription = hasROIVouchers
    ? defaultDescription.replaceAll('£', '€')
    : defaultDescription;

  const findOfferToNotify = (
    offersToNotify: OfferNotificationInfo[],
    userVouchersData: VoucherList | undefined
  ) => {
    const matchingPromotions =
      userVouchersData &&
      userVouchersData?.active
        .map(({ offerId }) => offersToNotify.find((offer) => offer.id === offerId))
        .filter(Boolean);
    if (matchingPromotions?.length) {
      return matchingPromotions[0];
    }
    return undefined;
  };

  const offerToNotify = findOfferToNotify(offerIdsInWallet, vouchersData);

  const { showPrompt, canPrompt, addToHomeScreen } = useAddToHomeScreen();

  const [hasViewedPopUp, setHasViewedPopUp] = useSessionState('hasViewedPopUp', false);
  const [notifications, updateNotification] = useNotifications();

  const hasViewedBirthdayModal = getFromStorage('hasViewedBirthdayModal');
  const hasViewedWinASliceModal = getFromStorage('hasViewedWinASliceModal');
  const hasViewedJet2holidaysModal = getFromStorage('hasViewedJet2holidaysModal');
  const hasViewedZizzi25thAnniversaryModal = getFromStorage('hasViewedZizzi25thAnniversaryModal');
  const hasViewedOfferInWalletModal = getFromStorage('hasViewedOfferInWalletModal');

  /**
   * STATE
   */
  const [showPerkzWalletModal, setShowPerkzWalletModal] = useState<boolean>(false);

  const [openModals, setOpenModals] = useState<ModalState>({
    perkzStore: false,
    purchaseVouchers: false,
    perkzWallet: false,
  });

  const [openPopUps, setOpenPopUps] = useState({
    surprise: false,
    birthday: false,
    refer: false,
    sale: false,
    winASlice: false,
    jet2holidays: false,
    zizzi25thAnniversary: false,
    offerInWallet: false,
  });

  const [currentOffer, setCurrentOffer] = useState<Offer>({
    id: 0,
    title: '',
    points: 0,
    imageUrl: '',
    description: '',
    conditions: '',
    isAlcohol: false,
  });

  /**
   * DATA FETCH
   */
  const fetchOffersCarouselData = useFetchFromService('offersCarouselData');

  const fetchVouchersData = useCallback(
    useFetchFromService('vouchersData', {
      retryHandler: handleVouchersRetries,
    }),
    []
  );

  const fetchWelcomeVoucher = useCallback(() => {
    fetchVouchersData();
    setToStorage('hasNewVouchers', true);
  }, [fetchVouchersData, setToStorage]);

  useEffect(() => {
    if (isNewSignUp) fetchWelcomeVoucher();
  }, [isNewSignUp, fetchWelcomeVoucher]);

  useEffect(() => {
    if (!offersCarouselData) fetchOffersCarouselData();
  }, [offersCarouselData, fetchOffersCarouselData]);

  useEffect(() => {
    if (!birthdayVoucher && hasViewedBirthdayModal) {
      removeFromStorage('hasViewedBirthdayModal');
    }
  }, [birthdayVoucher, hasViewedBirthdayModal, removeFromStorage]);

  useEffect(() => {
    if (allowWinASlice && hasViewedWinASliceModal) {
      removeFromStorage('hasViewedWinASliceModal');
    }
  }, [allowWinASlice, hasViewedWinASliceModal, removeFromStorage]);

  useEffect(() => {
    if (allowJet2holidays && hasViewedJet2holidaysModal) {
      removeFromStorage('hasViewedJet2holidaysModal');
    }
  }, [allowJet2holidays, hasViewedJet2holidaysModal, removeFromStorage]);

  useEffect(() => {
    if (allowAmountOffFoodVoucherNotification && hasViewedZizzi25thAnniversaryModal) {
      removeFromStorage('hasViewedZizzi25thAnniversaryModal');
    }
  }, [
    allowAmountOffFoodVoucherNotification,
    hasViewedZizzi25thAnniversaryModal,
    removeFromStorage,
  ]);

  useEffect(() => {
    if (allowOfferInWalletNotification && hasViewedOfferInWalletModal) {
      removeFromStorage('hasViewedOfferInWalletModal');
    }
  }, [allowOfferInWalletNotification, hasViewedOfferInWalletModal, removeFromStorage]);

  /**
   * NOTIFICATIONS
   */
  const showBirthdayNotification = !!birthdayVoucher;
  const showWelcomeNotification = (isNewSignUp && !!welcomeVoucher) || storedWelcomeNotification;
  const showWelcomeBonusNotification = allowWelcomeBonus && showWelcomeBonusZsNotification;
  const showJet2holidaysNotification = allowJet2holidays;
  const showWinASliceNotification = allowWinASlice;
  const showPercentOffFoodVoucherNotification =
    allowPercentOffFoodVoucherNotification && !!percentOffFoodVoucher;
  const showAmountOffFoodVoucherNotification =
    allowAmountOffFoodVoucherNotification && !!amountOffFoodVoucher;

  const getReferralTitle = useCallback(() => {
    if (!referralPointsEarned || !numberOfReferrals) return undefined;

    return numberOfReferrals > 1
      ? `Thanks for referring us to ${numberOfReferrals} friends! We have added ${referralPointsEarned} Zs to your balance`
      : `Thanks for referring us! We have added ${referralPointsEarned} Zs to your balance`;
  }, [referralPointsEarned, numberOfReferrals]);

  useEffect(() => {
    updateNotification('offer-in-wallet', {
      isVisible: !!offerToNotify,
      title: offerToNotify?.title,
      description: offerToNotify?.description,
      image: offerToNotify?.image,
    });
  }, [updateNotification, offerToNotify]);

  useEffect(() => {
    updateNotification('percent-off-food', { isVisible: showPercentOffFoodVoucherNotification });
  }, [updateNotification, showPercentOffFoodVoucherNotification]);

  useEffect(() => {
    updateNotification('amount-off-food', {
      isVisible: showAmountOffFoodVoucherNotification,
      title: amountOffFoodNotificationTitle,
      description: amountOffFoodNotificationDescription,
    });
  }, [
    updateNotification,
    showAmountOffFoodVoucherNotification,
    amountOffFoodNotificationTitle,
    amountOffFoodNotificationDescription,
  ]);

  useEffect(() => {
    updateNotification('surprise', { isVisible: isEligibleForSurpriseVoucher });
  }, [updateNotification, isEligibleForSurpriseVoucher]);

  useEffect(() => {
    updateNotification('birthday', {
      isVisible: showBirthdayNotification,
      onClick: () => setShowPerkzWalletModal(true),
    });
  }, [updateNotification, showBirthdayNotification]);

  useEffect(() => {
    updateNotification('welcome', {
      isVisible: showWelcomeNotification,
      title: `Welcome to the club! Here's a ${welcomeVoucher?.title} on the house.`,
      imageProps: { imgUrl: welcomeVoucher?.imageUrl },
      onClick: () =>
        welcomeVoucher &&
        pushAnalyticsEvent({ event: 'welcomeOfferClick', title: welcomeVoucher.title }),
    });
  }, [updateNotification, showWelcomeNotification, welcomeVoucher]);

  useEffect(() => {
    updateNotification('install', {
      ...(canPrompt && installWithPromptNotification),
      isVisible: showPrompt,
      onClick: () => addToHomeScreen('Carousel'),
    });
  }, [updateNotification, showPrompt, canPrompt, addToHomeScreen]);

  useEffect(() => {
    updateNotification('referral-bonus', {
      isVisible: !!referralPointsEarned,
      title: getReferralTitle(),
    });
  }, [updateNotification, referralPointsEarned, getReferralTitle]);

  useEffect(() => {
    updateNotification('sale', { isVisible: allowSaleOffers });
  }, [updateNotification, allowSaleOffers]);

  useEffect(() => {
    updateNotification('welcome-bonus', { isVisible: showWelcomeBonusNotification });
  }, [updateNotification, showWelcomeBonusNotification]);

  useEffect(() => {
    updateNotification('win-a-slice', { isVisible: showWinASliceNotification });
  }, [updateNotification, showWinASliceNotification]);

  useEffect(() => {
    updateNotification('jet2holidays', { isVisible: showJet2holidaysNotification });
  }, [updateNotification, showJet2holidaysNotification]);

  /**
   * MODALS
   */

  const surprisePerkType: SurprisePerkType = isEligibleForSurpriseVoucher
    ? 'surprise'
    : 'spin-to-win';
  const isSpinToWin =
    allowSpinToWin &&
    isSpinTheWheelPeriod &&
    showWelcomeNotification &&
    surprisePerkType === 'spin-to-win';
  const showSurprisePerkModal = (!!isEligibleForSurpriseVoucher || isSpinToWin) && !hasViewedPopUp;

  const showBirthdayPerkModal =
    showBirthdayNotification && !showSurprisePerkModal && !hasViewedPopUp;

  const showReferAndEarnModal =
    vouchersData &&
    isWithinDateRange('2023-05-18', '2023-05-31') &&
    !showSurprisePerkModal &&
    !showBirthdayPerkModal &&
    !hasViewedPopUp;

  const showSaleModal =
    vouchersData &&
    allowSaleOffers &&
    !showSurprisePerkModal &&
    !showBirthdayPerkModal &&
    !showReferAndEarnModal &&
    !hasViewedPopUp;

  const showWinASliceModal = allowWinASlice && !showSurprisePerkModal && !hasViewedPopUp;
  const showJet2holidaysModal = allowJet2holidays && !hasViewedPopUp;
  const showZizzi25thAnniversaryModal =
    showAmountOffFoodVoucherNotification && !allowJet2holidays && !hasViewedPopUp;
  const showOfferInWalletModal =
    allowOfferInWalletNotification && !!offerToNotify && !allowSaleOffers && !hasViewedPopUp;

  useEffect(() => {
    const popUpViewed = Object.values(openPopUps).some((isOpen) => isOpen);
    if (popUpViewed) setHasViewedPopUp(true);
  }, [openPopUps, setHasViewedPopUp]);

  useEffect(() => {
    if (showSurprisePerkModal) {
      setOpenPopUps({
        surprise: true,
        birthday: false,
        refer: false,
        sale: false,
        winASlice: false,
        jet2holidays: false,
        zizzi25thAnniversary: false,
        offerInWallet: false,
      });
    }
  }, [showSurprisePerkModal]);

  useEffect(() => {
    if (showBirthdayPerkModal) {
      setOpenPopUps({
        surprise: false,
        birthday: true,
        refer: false,
        sale: false,
        winASlice: false,
        jet2holidays: false,
        zizzi25thAnniversary: false,
        offerInWallet: false,
      });
      setToStorage('hasViewedBirthdayModal', true);
    }
  }, [showBirthdayPerkModal, setToStorage]);

  useEffect(() => {
    if (showJet2holidaysModal) {
      setOpenPopUps({
        surprise: false,
        birthday: false,
        refer: false,
        sale: false,
        winASlice: false,
        jet2holidays: true,
        zizzi25thAnniversary: false,
        offerInWallet: false,
      });
      setToStorage('hasViewedJet2holidaysModal', true);
    }
  }, [setToStorage, showJet2holidaysModal]);

  useEffect(() => {
    if (showReferAndEarnModal) {
      setOpenPopUps({
        surprise: false,
        birthday: false,
        refer: true,
        sale: false,
        winASlice: false,
        jet2holidays: false,
        zizzi25thAnniversary: false,
        offerInWallet: false,
      });
    }
  }, [showReferAndEarnModal]);

  useEffect(() => {
    if (showSaleModal) {
      setOpenPopUps({
        surprise: false,
        birthday: false,
        refer: false,
        sale: true,
        winASlice: false,
        jet2holidays: false,
        zizzi25thAnniversary: false,
        offerInWallet: false,
      });
    }
  }, [showSaleModal]);

  useEffect(() => {
    if (showWinASliceModal) {
      setOpenPopUps({
        surprise: false,
        birthday: false,
        refer: false,
        sale: false,
        winASlice: true,
        jet2holidays: false,
        zizzi25thAnniversary: false,
        offerInWallet: false,
      });
      setToStorage('hasViewedWinASliceModal', true);
    }
  }, [setToStorage, showWinASliceModal]);

  useEffect(() => {
    if (showZizzi25thAnniversaryModal) {
      setOpenPopUps({
        surprise: false,
        birthday: false,
        refer: false,
        sale: false,
        winASlice: false,
        jet2holidays: false,
        zizzi25thAnniversary: true,
        offerInWallet: false,
      });
      setToStorage('hasViewedZizzi25thAnniversaryModal', true);
    }
  }, [setToStorage, showZizzi25thAnniversaryModal]);

  useEffect(() => {
    if (showOfferInWalletModal) {
      setOpenPopUps({
        surprise: false,
        birthday: false,
        refer: false,
        sale: false,
        winASlice: false,
        jet2holidays: false,
        zizzi25thAnniversary: false,
        offerInWallet: true,
      });
      setToStorage('hasViewedOfferInWalletModal', true);
    }
  }, [setToStorage, showOfferInWalletModal]);

  /**
   * HANDLERS
   */
  const handleViewOffer = (offer: Offer) => {
    setCurrentOffer(offer);
    setOpenModals({ ...openModals, perkzStore: true });
    pushAnalyticsEvent({
      event: 'carouselOpenModal',
      reward: offer.title,
      offer: offer.title,
      price: offer.points,
    });
  };

  const handleOnSurprisePerkClose = () => {
    setOpenPopUps({ ...openPopUps, surprise: false });
  };

  const handleOnPerkzWalletToggle = () => {
    setShowPerkzWalletModal(!showPerkzWalletModal);
  };

  const handleOnReferAndEarnToggle = () => {
    setOpenPopUps({ ...openPopUps, refer: !openPopUps.refer });
  };

  const handleOnBirthdayModalToggle = () => {
    setOpenPopUps({ ...openPopUps, birthday: false });
  };

  const handleOnBirthdayPerkReveal = () => {
    handleOnBirthdayModalToggle();
    handleOnPerkzWalletToggle();
  };

  const handleWinASliceModalToggle = () => {
    setOpenPopUps({ ...openPopUps, winASlice: !openPopUps.winASlice });
  };

  const handleJet2HolidaysModalToggle = () => {
    setOpenPopUps({ ...openPopUps, jet2holidays: !openPopUps.jet2holidays });
  };

  const handleSaleModalToggle = () => {
    setOpenPopUps({ ...openPopUps, sale: !openPopUps.sale });
  };

  const handleZizzi25thAnniversaryModalToggle = () => {
    setOpenPopUps({ ...openPopUps, zizzi25thAnniversary: !openPopUps.zizzi25thAnniversary });
  };

  const handleOfferInWalletModalToggle = () => {
    setOpenPopUps({ ...openPopUps, offerInWallet: !openPopUps.offerInWallet });
  };

  const showNotificationCentre = !!notifications.length;

  return (
    <Layout ariaLabel="Welcome Page">
      {userData && offersCarouselData && vouchersData ? (
        <StyledContainer>
          <StyledPaper>
            <UserVisits userVisits={userData} />

            {showNotificationCentre && <NotificationCentre items={notifications} />}

            {offersCarouselData && (
              <>
                <OffersCarousel offers={offersCarouselData} onViewOffer={handleViewOffer} />
                <PurchaseFlowModal
                  currentOffer={currentOffer}
                  openModals={openModals}
                  setOpenModals={setOpenModals}
                />
              </>
            )}

            <StyledProfileCard user={userData} />

            <TertiaryButton
              fullWidth
              disableRipple
              color="primary"
              component={Link}
              to={ROUTES.TERMS_AND_CONDITIONS}
            >
              Terms &amp; Conditions
            </TertiaryButton>
          </StyledPaper>

          <SurpriseAlertModal
            isOpen={openPopUps.surprise}
            onClose={handleOnSurprisePerkClose}
            type={surprisePerkType}
          />

          <BirthdayModal
            isOpen={openPopUps.birthday}
            onClose={handleOnBirthdayModalToggle}
            onNext={handleOnBirthdayPerkReveal}
          />

          <PerkzWalletModal
            isOpen={showPerkzWalletModal}
            onClose={handleOnPerkzWalletToggle}
            voucher={birthdayVoucher}
          />

          <ScheduledPerkModal
            isOpen={openPopUps.jet2holidays}
            onClose={handleJet2HolidaysModalToggle}
            content={jet2holidaysNotification}
          />

          <ReferAndEarnModal isOpen={openPopUps.refer} onClose={handleOnReferAndEarnToggle} />

          <ScheduledPerkModal
            isOpen={openPopUps.winASlice}
            onClose={handleWinASliceModalToggle}
            content={winASliceNotification}
          />

          <ScheduledPerkModal
            isOpen={openPopUps.sale}
            onClose={handleSaleModalToggle}
            content={saleNotification}
          />

          <ScheduledPerkModal
            isOpen={openPopUps.zizzi25thAnniversary}
            onClose={handleZizzi25thAnniversaryModalToggle}
            content={{
              ...amountOffFoodPromotionNotification,
              title: amountOffFoodNotificationTitle,
              description: amountOffFoodNotificationDescription,
            }}
          />

          <ScheduledPerkModal
            isOpen={openPopUps.offerInWallet}
            onClose={handleOfferInWalletModalToggle}
            content={{
              ...offerInWalletNotification,
              title: offerToNotify ? offerToNotify?.title : 'Enjoy an offer on us!',
              description: offerToNotify
                ? offerToNotify?.description
                : 'Please visit your perkz wallet to view the perk',
              image: offerToNotify ? offerToNotify.image : '',
            }}
          />
        </StyledContainer>
      ) : (
        <Loader />
      )}
    </Layout>
  );
}

export default Welcome;
